import React, { useMemo } from 'react';
import { Column, Row } from 'react-table';
import { UserInfoDto } from 'services/data-contracts';
import TableBody from 'components/table/TableBody';
import { formatNumberWithCommas } from 'libs/helper/utils';
import { EditButton, PaginationContainer } from 'resources/styles/TableStyle';
import PaginationWrapper from '../Pagination';
import { observer } from 'mobx-react';
import { useStoreWrapper } from 'stores/StoreHelper';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import { Row as SaladRow } from 'saladsoft-primitive';
interface UserTableProps {
  users: UserInfoDto[];
  onEditBtnClick?: (user: UserInfoDto) => void;
}

// 테이블 데이터 타입 정의
const UsersTable = observer(({ users, onEditBtnClick }: UserTableProps) => {
  const { userFilterStore } = useStoreWrapper();

  const isAdmin = users.some((user) => user.roleType === 'ADMIN');

  // 테이블 컬럼 정의
  const columns = useMemo(
    () => [
      {
        Header: 'No',
        accessor: 'id' as keyof UserInfoDto,
        width: 30,
        Cell: ({ row }: { row: Row<UserInfoDto> }) => row.index + 1,
      },
      ...(users.some(user => user.roleType) ? [{
        Header: '역할',
        accessor: 'roleType' as keyof UserInfoDto,
        width: 100,
        Cell: ({ value }: { value: string }) => {
          return value === 'ADMIN' ? <SaladRow gap={5} justifyContent='flex-start'>
            <VerifiedUserIcon style={{ color: "var(--point-color)" }} /><div>관리자</div></SaladRow> :
            <SaladRow gap={5} justifyContent='flex-start'>
              <AssignmentIndOutlinedIcon style={{ color: "#999" }} /><div>매니저</div></SaladRow>;
        },
      }] : []),
      ...(isAdmin
        ? []
        : [
          {
            Header: 'Social',
            accessor: 'provider' as keyof UserInfoDto,
            width: 100,
            Cell: ({ value }: { value: string }) => {
              // 추후 수정 필요 (관리자 계정은 social 없음)
              return value === 'KAKAO' ? 'Kakao' : 'Google';
            },
          },
        ]),
      {
        Header: '이메일',
        accessor: 'username' as keyof UserInfoDto,
        width: 200,
      },
      ...(!isAdmin
        ? []
        : [
          {
            Header: '상태',
            accessor: 'isWithdrawn' as keyof UserInfoDto,
            width: 100,
            Cell: ({ value }: { value: boolean }) => {
              let backgroundColor = '#e6f7ff';
              let color = '#1890ff';

              if (value) {
                backgroundColor = '#fff1f0';
                color = '#ff4d4f';
              }

              return (
                <div
                  style={{
                    backgroundColor,
                    color,
                    padding: '4px 8px',
                    borderRadius: '4px',
                    display: 'inline-block',
                  }}
                >
                  {value ? 'INACTIVE' : 'ACTIVE'}
                </div>
              );
            },
          },
        ]),
      {
        Header: '회원명',
        accessor: 'profile.firstName' as keyof UserInfoDto,
        width: 120,
        Cell: ({ row }: { row: any }) => {
          return `${row.original.profile?.lastName || ''} ${row.original.profile?.firstName || ''}`;
        },
      },
      ...(isAdmin
        ? []
        : [
          {
            Header: '등급',
            accessor: 'membershipLevel' as keyof UserInfoDto,
            width: 80,
          },
        ]),
      {
        Header: '연락처',
        accessor: 'profile.phoneNumber' as keyof UserInfoDto,
        width: 150,
      },
      {
        Header: isAdmin ? '생성일' : '가입일',
        accessor: 'signupDate' as keyof UserInfoDto,
        width: 120,
        Cell: ({ value }: { value: string }) => {
          const date = new Date(value);
          return date.toLocaleDateString('ko-KR');
        },
      },
      ...(isAdmin
        ? []
        : [
          {
            Header: '생일',
            accessor: 'profile.birthday' as keyof UserInfoDto,
            width: 120,
            Cell: ({ value }: { value: string | null }) => {
              return value ? value : '-';
            },
          },
        ]),
      ...(isAdmin
        ? []
        : [
          {
            Header: '추천인',
            accessor: 'referrerEmail' as keyof UserInfoDto,
            width: 120,
            Cell: ({ value }: { value: string | null }) => {
              return value ? value : '-';
            },
          },
        ]),
      ...(isAdmin
        ? []
        : [
          {
            Header: '보유포인트',
            accessor: 'point' as keyof UserInfoDto,
            width: 120,
            Cell: ({ value }: { value: number }) => {
              return `${formatNumberWithCommas(value)} P`;
            },
          },
        ]),
      {
        Header: '수정',
        id: 'edit',
        width: 80,
        Cell: ({ row }: { row: any }) => (
          <EditButton
            onClick={(e) => {
              e.stopPropagation();
              if (onEditBtnClick) {
                onEditBtnClick(row.original);
              }
            }}
          >
            수정
          </EditButton>
        ),
      },
    ],
    [users, isAdmin],
  ) as Column<UserInfoDto>[];

  return (
    <>
      <div style={{ paddingBottom: '100px' }}>
        <TableBody columns={columns} data={users} />
      </div>

      {!['active', 'withdrawn'].includes(userFilterStore.adminUserConditions?.type || '') && (
        <PaginationContainer>
          <PaginationWrapper
            count={userFilterStore.paginationInfo?.totalCount ?? 0}
            totalPages={userFilterStore.paginationInfo?.totalPages ?? 0}
          />
        </PaginationContainer>
      )}
    </>
  );
});

export default UsersTable;
